import React, { useState,useRef, useEffect} from "react";
import { Formik, Form, useField, ErrorMessage } from "formik";
import * as Yup from "yup";
import styles from "./orderForm.module.css";
import {TextField,DateTimeTextField,AgreementStatusControl, TextArea, notInPast, mobileNumberValidator} from "../../controllers/quotationControllers";
import FileUploadAttachment from "../../../../components/fileUpload/fileUpload.container"
import { BackButton, Submit } from "../../../../components/button/buttons";
import { ACCORDIAN_TITLE, ACTION_STATUS, DEMAND_ACTIONS, TYPE } from "../../../../utils/constants/DemandConstant";
import { FUNCTIONAL_ROLES_CONSTANTS, USER_TYPE } from "../../../../utils/constants/userConstants";
import { formatDate, formatDateInDMY, generateOrderFormPDF, has, hasAny } from "../../../../utils/arrayUtils";
import { useParams } from "react-router-dom";
import Asterisk from "../../../../components/asterisk/Asterisk";
import IMAGES from "../../../../assets/images/images";
import Accordion from "../../../../components/accordion/Accordion";
import CAN from "../../../../casl/can";
import { Action, Resource } from "../../../../casl/constants";
import { ConfirmDialog } from "../../../../hooks/confirmDialog/confirmDialog";
import { connect } from "react-redux";
import { openAlertPopup } from "../../../../redux/actions/Alert/alert.action";
import { addDemandAction } from "../../../../redux/actions/demand/demand.action";
import DatePickerField from '../../../../components/datePicker/DatePickerField'
import { FormErrorLabel } from "../../../../components/label/Labels";
import { getLogo } from "../../../../utils/constants/fileTypeLogo";
/* -------------------- Validation (Yup) -------------------- */
const getSchema = (isCustomer,isCoreSectionsEditable) =>
  Yup.object({
  vfPartyName: Yup.string().required("Required"),
  customerName: Yup.string().required("Required"),
  agreementStatus: Yup.object({
    status: Yup.string()
      .oneOf(["signed", "to_be_signed"])
      .required("Required"),
    signedDate: Yup.string()
      .nullable()
      .when("status", {
        is: "signed",
        then: (schema) => schema.required("Signed Date is required"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
  }),
  commencementDate: Yup.string()
    .required("Required"),
  termMonths: Yup.string()
    .required("Required"),
  technicalContact: Yup.string(),
  accountManager: Yup.object({
    name: Yup.string().max(100, "Name must be at most 100 characters").required("Required"),
    title: Yup.string().max(100, "Title must be at most 100 characters").required("Required"),
    mobile: mobileNumberValidator.required("Required"),
    email: Yup.string().email("Enter a valid email").required("Required"),
  }),
  serviceOffering: Yup.object({
    details: Yup.string().max(1000, "Details must be at most 1000 characters").required("Required"),
  }),
  customerCharges: Yup.object({
    nonRecurringCharge: Yup.string().max(250, "Non-Recurring Charge must be at most 250 characters").required("Required"),
    monthlyCharge: Yup.string().max(250, "Monthly-Recurring Charge must be at most 250 characters").required("Required"),
  }),
  installationAddress: Yup.object({
  popAddress: Yup.string().max(1000, "PoP Address must be at most 1000 characters"),
  popAddressImage: Yup.array().of(Yup.mixed()).nullable(),
}).test(
  "address-or-image-required",
  "Either PoP Address or PoP Address Image is required",
  function (value) {
    const { popAddress, popAddressImage } = value || {};
    const hasText = popAddress && popAddress.trim() !== "";
    const hasFiles = Array.isArray(popAddressImage) && popAddressImage.length > 0;
    return hasText || hasFiles; // MUST return true to pass validation
  }
),
  additionalTerms: Yup.string().required("Required"),
  vodafoneSignature: Yup.object({
    legalEntity: Yup.string().required("Required"),
    registeredNumber: mobileNumberValidator.required("Required"),
    country: Yup.string().required("Required"),
    registeredAddress: Yup.string().max(1000, "Registered address must be at most 1000 characters").required("Required"),
    name: Yup.string().max(100, "Name must be at most 100 characters").required("Required"),
    title: Yup.string().max(100, "Title must be at most 100 characters").required("Required"),
    date: (isCoreSectionsEditable ? notInPast : Yup.string()).required("Required"),
  }),
  customerSignature: Yup.object({
    legalEntity: Yup.string()
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    registeredNumber: mobileNumberValidator
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    country: Yup.string()
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    registeredAddress: Yup.string()
      .max(1000, "Registered address must be at most 1000 characters")
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    name: Yup.string()
      .max(100, "Name must be at most 100 characters")
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    title: Yup.string()
      .max(100, "Title must be at most 100 characters")
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
    }),
    date: notInPast
      .when([], {
        is: () => isCustomer,
        then: s => s.required("Required"),
        otherwise: s => s.notRequired().nullable(),
      }),
    })
});

/* -------------------- Component -------------------- */
const OrderForm = ({demandDetail,handelServiceDetailapi,orderFormData:initialData,onFileDownload,...props})=>{
  const [orderFormData,setOrderFormData] = useState(initialData)
  let { requestId } = useParams();
  const [isPreview, setIsPreview] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const canCreateOrderForm = CAN(Action.CREATE_DRAFT_ORDER_FORM, Resource.D2C_DEMANDS);
  useEffect(()=>{
    setOrderFormData(initialData)
  },[initialData])
  const isCustomer = props?.myRights?.userType === USER_TYPE.CUSTOMER;
  const canEditCoreSections = canCreateOrderForm && demandDetail?.actionStatus === ACTION_STATUS.ORDER_FORM_CREATION_INITIATED;
  const canEditSignatureSection = (canCreateOrderForm && demandDetail?.actionStatus === ACTION_STATUS.ORDER_FORM_CREATION_INITIATED) || (isCustomer && (demandDetail.actionStatus===ACTION_STATUS.ORDER_FORM_CUSTOMER_APPROVAL_INITIATED));
  // ---- Apply Preview overlay: when preview is ON, you cannot edit even if base allows it
  const isCoreSectionsEditable = canEditCoreSections  && !isPreview;
  const isSignatureSectionEditable = canEditSignatureSection  && !isPreview;
  // For UI helpers
  const canPreview = canEditCoreSections || canEditSignatureSection ;
  const isAllReadOnly = !isCoreSectionsEditable && !isSignatureSectionEditable;

  const onSubmit = async (values) => {
    if (!isPreview && canPreview) {
      setIsPreview(true);
      return;
    }
    const nextStatus = canCreateOrderForm ? DEMAND_ACTIONS.COMPLETE_DRAFT_ORDERFORM : isCustomer? DEMAND_ACTIONS.SAVE_ORDERFORM : "";
    const ok = await ConfirmDialog(nextStatus);
    if (!ok.confirmed) return false
    const actionComment=ok?.comment.trim()  // if confirmed
    setIsSubmit(true)
     try {
      const payload = {
        action:nextStatus,
        stateData: isCustomer ? {accepted: true,orderFormDocUniqueId:values?.orderFormDocUniqueId,customerSignature:values?.customerSignature,} : {...values,actionComment:actionComment}
      }
      const res = await props.addDemandAction(payload, requestId);
      if (!res) {
            props.openAlertPopup({
              message: "Unable to process order form. Please try again.",
              image: IMAGES.error,
            });
            return;
          }
      isCustomer && generateOrderFormPDF(values, {fileName: `OrderForm_IPX_${values?.customerName || "Customer"}.pdf`,logoSrc: IMAGES.logo,badgeSrc: IMAGES.badge,generatedBy: "Vodafone IPX Portal",})
      await handelServiceDetailapi();
      props.openAlertPopup({
        message: canCreateOrderForm ? "Order Form submitted successfully." : isCustomer ? "Order Form saved and downloaded successfully.": "",
        image: IMAGES.success,
      });

    } catch (error){
      props?.openAlertPopup({
        message:"An error occurred while processing your request. Please try again later.",
        image: IMAGES.error,
      });
    } finally {
      setIsSubmit(false)
    }
  };
  const SignedOrderFormSchema = Yup.object({
    isSigned: Yup.boolean().required().oneOf([true], "Please select checkbox"),
    files: Yup.array()
      .min(1, "File is required")
      .required("File is required"),
  });

  const downloadFile = (file, index, onDownload) => {
    onDownload(file)
  }
  
  return (
    <>
     {CAN(Action.UPLOAD_SIGNED_ORDERFORM_ATTACHMENTS, Resource.D2C_DEMANDS) && demandDetail?.actionStatus === ACTION_STATUS.ORDER_FORM_UNSIGNED_SAVED_BY_CUSTOMER &&
      <div className={styles.container}>
        <Formik
          initialValues={{
            isSigned: false,
            files: []
          }}
          validationSchema={SignedOrderFormSchema}
          onSubmit={async (values,{setSubmitting}) => {
            const ok = await ConfirmDialog(DEMAND_ACTIONS.SUBMIT_SIGNED_ORDERFORM);
            if (!ok.confirmed) return false
            setIsSubmit(true);
            try {
              const payload = {
                action:DEMAND_ACTIONS.SUBMIT_SIGNED_ORDERFORM,
                stateData: {...values,orderFormDocUniqueId:orderFormData?.orderFormDocUniqueId}
              }
              const res = await props.addDemandAction(payload, requestId);
              if (!res) {
                props.openAlertPopup({
                  message: "Unable to process. Please try again.",
                  image: IMAGES.error,
                });
                return;
              }
              await handelServiceDetailapi();
              props.openAlertPopup({
                message: "Signed order form submitted successfully.",
                image: IMAGES.success,
              });
            } catch (error){
              props?.openAlertPopup({
                message:"An error occurred while processing your request. Please try again later.",
                image: IMAGES.error,
              });
            } finally {
              setIsSubmit(false);
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit,isValid, dirty,isSubmitting}) => (
            <Form onSubmit={handleSubmit}>
              <div className={styles.wrapper}>
                <div className={styles.sectionheading}>
                  Please upload the signed order form here to proceed.<Asterisk />
                </div>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    name="isSigned"
                    checked={values.isSigned}
                    onChange={(e) =>
                      setFieldValue("isSigned", e.target.checked)
                    }
                  />
                  <span className={styles.checjboxlabel}>Please confirm whether you have signed the order form.</span>
                </label><br/>
                <ErrorMessage name ="isSigned">
                    {(msg)=><FormErrorLabel text={msg} />}
                </ErrorMessage>
                 <FileUploadAttachment
                  setFieldValue={setFieldValue}
                  name="files"
                  value={values.files}
                  note={false}
                  displayMessage="Upload limit: 1 PDF file, up to 5 MB."
                  maxFileSize={5 * 1024 * 1024}
                  maxFileSizeTXT="5MB"
                  combineFilesMaxSize={5 * 1024 * 1024}
                  numOfFilesAllowToUpload={1}
                  errorMessageForNumberOfFiles="You can upload up to one file."
                  acceptFileType={{
                    'application/pdf': ['.pdf'],
                  }}
                />
                <ErrorMessage name ="files">
                    {(msg)=><FormErrorLabel text={"Please upload file"} />}
                </ErrorMessage>
                <div className={styles.footerupload}>
                  <Submit type={"submit"} disabled={!dirty} className={styles.saveanddownload} title={"Upload"} isSubmitting={isSubmit}/>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>}
      <div className={styles.container}>
        <Accordion accordianType={TYPE.PARENT} title={ACCORDIAN_TITLE.ORDER_FORM}s defaultOpen={canPreview}>
           {/* {orderFormData?.createdAt &&<div className={styles.createdwrapper}>
                    <span className={styles.datelabel}>Created On:</span>
                    <span className={styles.datevalue}>{formatDateInDMY(orderFormData?.createdAt)}</span>
                  </div>
            } */}
          {
            orderFormData?.actionComment && orderFormData?.actionComment.trim().length > 0 &&
            (<div className={styles.commentText}>
                  <span className={styles.commentlabel}>Comment</span> 
                  <div className={styles.commentvalue}>{orderFormData?.actionComment?.split('\n').map(text => <div>{text}</div>)}</div>
                </div>)
          }
          <div className={styles.header}>
          <p className={styles.muted}>
            This Order Form includes and is governed by the terms and conditions in an Agreement — MSA/IPX Service Terms.
          </p>
        </div>
        <Formik initialValues={orderFormData} validationSchema={getSchema(isCustomer,isCoreSectionsEditable)} validateOnMount={true} onSubmit={onSubmit}>
          {({ values, setFieldValue, isSubmitting,isValid, dirty, }) => (
            <Form noValidate>
              {/* ---------- 0) Parties ---------- */}
              <section className={`${styles.orderform} ${!isCoreSectionsEditable?styles.previewmode:""}`}>
                <div className={styles.grid2}>
                  <div className={styles.wrapperpadding}>
                  <TextField
                    name="vfPartyName"
                    label="Vodafone Entity"
                    placeholder="Enter here"
                    isPreviewMode={!isCoreSectionsEditable}
                    isMandatory={isCoreSectionsEditable}
                  />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField
                    name="customerName"
                    label="Organisation Name"
                    placeholder="Enter here"
                    isPreviewMode={!isCoreSectionsEditable}
                    isMandatory={isCoreSectionsEditable}
                  />
                  </div>
                </div>
                  <div className={!isCoreSectionsEditable && styles.grid2}>
                <div className={styles.wrapperpadding}>
                <AgreementStatusControl
                  nameStatus="agreementStatus.status"
                  nameDate="agreementStatus.signedDate"
                  label="Agreement – MSA/IPX Service Status"
                  isMandatory={isCoreSectionsEditable}
                  isPreviewMode={!isCoreSectionsEditable}
                  disabled={isPreview}
                />
                </div>
                {!isCoreSectionsEditable &&
                 <div className={styles.wrapperpadding}>
                    {orderFormData?.createdAt &&<div className={styles.createdwrapper}>
                    <span className={styles.datelabel}>Created On</span>
                    <span className={styles.datevalue}>{formatDate(orderFormData?.createdAt)}</span>
                  </div>}
                 </div>}
                 </div>
                {/* {isCoreSectionsEditable &&( */}
                <div className={styles.note}>
                  Until the carrier agreement is signed, the carrier general terms and IPX service terms (Sales) apply, available at{" "}
                  <a
                    href="https://www.vodafone.com/business/carrier-terms"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.notelink}
                  >
                    https://www.vodafone.com/business/carrier-terms
                  </a>. These terms will be replaced with the terms and conditions set out in the carrier agreement once countersigned.
                </div>
                {/* )} */}
              </section>
              {/* ---------- 1) Requested Service Commencement Date ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>1. Requested Service Commencement Date {isCoreSectionsEditable && <Asterisk/>}</div>
                {isCoreSectionsEditable &&(
                <div className={styles.optionalnote}>
                  <div className={styles.optionalnotetxt}>Add a date (With Optional Notes)</div>
                </div>)}
                <div className={styles.wrapperpadding}>
                <TextField
                  name="commencementDate"
                  type="text"
                  label={null}
                  placeholder={"dd/mm/yyy"}
                  isPreviewMode={!isCoreSectionsEditable}
                  isMandatory={isCoreSectionsEditable}
                />
                </div>
              </section>
              {/* ---------- 2) Minimum Term & Contacts ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>2. Minimum Term {isCoreSectionsEditable && <Asterisk/>}</div>
                <div className={styles.note}>Bandwidth capacity to be reviewed and upgraded where necessary in accordance with the terms and conditions of the underlying agreements.</div>
                {isCoreSectionsEditable &&(
                <div className={styles.optionalnote}>
                  <div className={styles.optionalnotetxt}>Add term in months (With Optional Notes)</div>
                </div>)}
                <div className={`${!isCoreSectionsEditable ? styles.previewmode:""}`}>
                  <div className={styles.wrapperpadding}>
                  <TextField
                    name="termMonths"
                    type="text"
                    label={null}
                    placeholder="e.g., 24"
                    isPreviewMode={!isCoreSectionsEditable}
                    isMandatory={isCoreSectionsEditable}
                  />
                  </div>
                <div>
                {/* <div className={styles.note}>Bandwidth capacity to be reviewed and upgraded where necessary in accordance with the terms and conditions of the underlying agreements.</div> */}
                </div>
                </div>
              </section>
              {/* ---------- 3) Contact Information ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>3. Contact Information</div>
                <div className={`${!isCoreSectionsEditable?styles.grid2:""} ${!isCoreSectionsEditable ? styles.previewmode:""}`}>
                <div className={styles.field}>
                <div className={styles.uploadtxt}>Technical Contact (URL)</div>
                <div className={styles.note}>
                  <a
                    href={values?.technicalContact}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.notelink}
                  >
                    {values?.technicalContact}
                  </a>
                </div>
                </div>
                </div>
                <div className={styles.subheading}>Account Manager</div>
                <div className={!isCoreSectionsEditable?styles.previewmode:""}>
                <div className={styles.grid2}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="accountManager.name" label="Name" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} textLimit={`${values?.accountManager?.name?.length}/100 characters`}/>
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="accountManager.title" label="Title" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} textLimit={`${values?.accountManager?.title?.length}/100 characters`}/>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="accountManager.mobile" label="Mobile" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="accountManager.email" type="email" label="Email" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} />
                  </div>
                </div>
                </div>
              </section>
              {/* ---------- 4) Service Offering (single row) ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>4. Service Offering {isCoreSectionsEditable && <Asterisk/>}</div>
                <p className={styles.muted}>
                  The IPX services in accordance with the Terms and Conditions, and associated Schedules for the services detailed below:
                </p>
                {isCoreSectionsEditable &&(
                <div className={styles.optionalnote}>
                  <ul className={styles.Offeringnotetxt}>
                    <li className={styles.optionalnotetxt}>No. of Ports / Quantity X bandwidth (Gbps)</li>
                    <li className={styles.optionalnotetxt}>Allocated IPX Transport Bandwidth (Gbps)</li>
                    <li className={styles.optionalnotetxt}>Optional Notes</li>
                  </ul>
                </div>)}
                <div className={`${!isCoreSectionsEditable?styles.previewmode:""} ${styles.wrapperpadding}`}>
                <TextArea
                  name="serviceOffering.details"
                  label={null}
                  placeholder={`Enter here`}
                  isPreviewMode={!isCoreSectionsEditable}
                  isMandatory={isCoreSectionsEditable}
                  textLimit={`${values?.serviceOffering?.details?.length}/1000 characters`}
                  isExpandedField
                />
                </div>
              </section>
              {/* ---------- 5) Charges ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>5. Charges (Payable by the Customer to Supplier){isCoreSectionsEditable && <Asterisk/>}</div>
                <div className={styles.chargesnote}>The Customer will pay Supplier the following Charges in accordance with the term and conditions. The full offer is attached to this orderform</div>
                <div className={!isCoreSectionsEditable?styles.previewmode:""}>
                <div className={styles.grid2}>
                  <div className={styles.wrapperpadding}>
                  <TextArea
                    name="customerCharges.nonRecurringCharge"
                    type="text"
                    placeholder="Enter here"
                    label="Non‑Recurring Charge (NRC) (With optional notes)"
                    isPreviewMode={!isCoreSectionsEditable}
                    textLimit={`${values?.customerCharges?.nonRecurringCharge?.length}/250 characters`}
                    isMandatory={isCoreSectionsEditable}
                  />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextArea
                    name="customerCharges.monthlyCharge"
                    type="text"
                    label="Monthly Recurring Charge (MRC) (With optional notes)"
                    placeholder="Enter here"
                    isPreviewMode={!isCoreSectionsEditable}
                    textLimit={`${values?.customerCharges?.monthlyCharge?.length}/250 characters`}
                    isMandatory={isCoreSectionsEditable}
                  />
                  </div>
                </div>
                </div>
              </section>
              {/* ---------- 6) Installation Address ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>6. Installation Address {isCoreSectionsEditable && <Asterisk/>}</div>
                <div className={!isCoreSectionsEditable?styles.previewmode:""}>
                <div className={styles.labeltxt}>PoP Address</div>
                <div className={styles.wrapperpadding}>
                <TextArea
                  name="installationAddress.popAddress"
                  // label="PoP Address"
                  placeholder="Enter here"
                  isPreviewMode={!isCoreSectionsEditable}
                  textLimit={`${values?.installationAddress?.popAddress?.length}/1000 characters`}
                  isMandatory={isCoreSectionsEditable}
                />
                </div>
                </div>
                {(isCoreSectionsEditable || !isCoreSectionsEditable&&values.installationAddress?.popAddressImage?.length>0) &&
                <>
                <div className={styles.orfield}>Or</div>
                <div className={styles.field}>
                  <div className={styles.uploadtxt}>Upload Image</div>
                  {!isCoreSectionsEditable && values.installationAddress?.popAddressImage?.length > 0 ?
                    values.installationAddress?.popAddressImage?.map((item, index) =>
                      <div className={styles.attachment} key={index} title={item?.originalFileName}>
                        <img className={styles.attachlogos} src={getLogo(item?.originalFileName)} />
                        <span className={styles.filename}>{item?.originalFileName}</span>
                        <img className={styles.attachlog} src={IMAGES.downloadlogo} onClick={() => downloadFile(item, index, onFileDownload)} />
                      </div>):
                  <FileUploadAttachment
                    setFieldValue={setFieldValue}
                    name="installationAddress.popAddressImage"
                    value={values.installationAddress?.popAddressImage}
                    note={false}
                    displayMessage="Only 1 image file max of 5 MB limit."
                    maxFileSize={5 * 1024 * 1024}
                    maxFileSizeTXT="5MB"
                    combineFilesMaxSize={5 * 1024 * 1024}
                    // combineFilesMaxSizeTXT="Combined file size exceeds 30MB"
                    numOfFilesAllowToUpload={1}
                    errorMessageForNumberOfFiles="You can upload up to 1 files."
                    acceptFileType={{
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                      // "image/webp": [".webp"],
                    }}
                    isPreviewMode={!isCoreSectionsEditable}
                  />}
                </div>
                </>}
              </section>
              {/* ---------- 7) Additional Terms ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>7. Additional Terms</div>
                <div className={!isCoreSectionsEditable?styles.previewmode:""}>
                <TextArea
                  name="additionalTerms"
                  label="Terms"
                  isMandatory={isCoreSectionsEditable}
                  placeholder="Enter here"
                  // isPreviewMode={isPreview}
                  isPreviewMode={!isCoreSectionsEditable}
                />
                </div>
              </section>
              {/* ---------- 8) Vodafone Signatures ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>8. Vodafone Signatures</div>
                <p className={styles.muted}>
                  Agreed by the parties and signed by their duly authorised representatives.
                </p>
                <div className={!isCoreSectionsEditable?styles.previewmode:""}>
                <div className={styles.grid3}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="vodafoneSignature.legalEntity" label="Legal Entity Name" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="vodafoneSignature.registeredNumber" label="Registered Number" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="vodafoneSignature.country" label="Country of Organisation" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} />
                  </div>
                </div>
                <div className={`${styles.field} ${styles.textareapadding} ${styles.bottommargin}`}>
                <TextArea
                  name="vodafoneSignature.registeredAddress"
                  label="Registered Address"
                  placeholder="Enter here"
                  isPreviewMode={!isCoreSectionsEditable}
                  textLimit={`${values?.vodafoneSignature?.registeredAddress?.length}/1000 characters`}
                  isMandatory={isCoreSectionsEditable}
                />
                </div>
                </div>
                {!isCoreSectionsEditable&&(
                <div className={`${styles.previewmode} ${styles.seprate}`}>
                  <div className={styles.signaturesection}>
                    <div className={styles.signlabel}>Signature</div>
                    <div className={styles.signaturespace}></div>
                  </div>
                </div>)}
                <div className={`${!isCoreSectionsEditable?styles.previewmode:""} ${styles.seprate}`}>
                <div className={styles.grid3}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="vodafoneSignature.name" label="Name" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} textLimit={`${values?.vodafoneSignature?.name?.length}/100 characters`}/>
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="vodafoneSignature.title" label="Title" placeholder="Enter here" isPreviewMode={!isCoreSectionsEditable} isMandatory={isCoreSectionsEditable} textLimit={`${values?.vodafoneSignature?.title?.length}/100 characters`}/>
                  </div>
                  {/* <div className={styles.wrapperpadding}>
                  <DateTimeTextField
                    name="vodafoneSignature.date"
                    type="date"
                    label="Date"
                    isPreviewMode={!isCoreSectionsEditable}
                  />
                  </div> */}
                   <div className={styles.wrapperpadding}>
                    <DatePickerField
                    label={"Date"}
                    isMandatory={isCoreSectionsEditable}
                    name="vodafoneSignature.date"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/MM/yyyy"
                    isPreviewMode={!isCoreSectionsEditable}
                    labelstyle={styles.labelstyle}
                   />
                   </div>
                </div>
                </div>
              </section>
              {/* ---------- 9) Customer Signatures ---------- */}
              <section className={styles.section}>
                <div className={styles.sectionheading}>9. Customer Signatures</div>
                <p className={styles.muted}>
                  Agreed by the parties and signed by their duly authorised representatives.
                </p>
                <div className={!isSignatureSectionEditable?styles.previewmode:""}>
                <div className={styles.grid3}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="customerSignature.legalEntity" label="Legal Entity Name" placeholder="Enter here" isPreviewMode={!isSignatureSectionEditable} isMandatory={isSignatureSectionEditable && isCustomer} />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="customerSignature.registeredNumber" label="Registered Number" placeholder="Enter here" isPreviewMode={!isSignatureSectionEditable} isMandatory={isSignatureSectionEditable && isCustomer} />
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="customerSignature.country" label="Country of Organisation" placeholder="Enter here" isPreviewMode={!isSignatureSectionEditable} isMandatory={isSignatureSectionEditable && isCustomer} />
                  </div>
                </div>
                <div className={`${styles.field} ${styles.textareapadding} ${styles.bottommargin}`}>
                  <TextArea
                    name="customerSignature.registeredAddress"
                    label="Registered Address"
                    placeholder="Enter here"
                    isPreviewMode={!isSignatureSectionEditable}
                    textLimit={`${values?.customerSignature?.registeredAddress?.length}/1000 characters`}
                    isMandatory={isSignatureSectionEditable && isCustomer}
                  />
                </div>
                </div>
                {!isSignatureSectionEditable&&(
                <div className={`${styles.previewmode} ${styles.seprate}`}>
                  <div className={styles.signaturesection}>
                    <div className={styles.signlabel}>Signature</div>
                    <div className={styles.signaturespace}></div>
                  </div>
                </div>)}
                <div className={`${!isSignatureSectionEditable?styles.previewmode:""} ${styles.seprate}`}>
                <div className={styles.grid3}>
                  <div className={styles.wrapperpadding}>
                  <TextField name="customerSignature.name" label="Name" placeholder="Enter here" isPreviewMode={!isSignatureSectionEditable} isMandatory={isSignatureSectionEditable && isCustomer} textLimit={`${values?.customerSignature?.name?.length}/100 characters`}/>
                  </div>
                  <div className={styles.wrapperpadding}>
                  <TextField name="customerSignature.title" label="Title" placeholder="Enter here" isPreviewMode={!isSignatureSectionEditable} isMandatory={isSignatureSectionEditable && isCustomer} textLimit={`${values?.customerSignature?.title?.length}/100 characters`}/>
                  </div>
                  <div className={styles.wrapperpadding}>
                  {/* <DateTimeTextField
                    name="customerSignature.date"
                    type="date"
                    label="Date"
                    isPreviewMode={!isSignatureSectionEditable}
                    isMandatory={isSignatureSectionEditable}
                  /> */}
                  <DatePickerField
                    name="customerSignature.date"
                    type="date"
                    label="Date"
                    isPreviewMode={!isSignatureSectionEditable}
                    isMandatory={isSignatureSectionEditable && isCustomer}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/MM/yyyy"
                    labelstyle={styles.labelstyle}
                  />
                  </div>
                </div>
                </div>
              </section>
              {/* Actions */}
              <div className={styles.actions}>
                <div className={`${styles.footerwrapper}`}>
                  {canEditCoreSections &&
                  <div className={`${styles.footer}`}>
                    {!isPreview && canPreview ? null : <BackButton className={styles.backButton} closePopup={() => setIsPreview(false)} title={"Edit"} disabled = {isSubmit}/>}
                    <Submit className={styles.sendToCustomerBtn} title={!isPreview && canPreview ? "Preview" : "Send To Customer"}isSubmitting={isSubmit}/>
                  </div>}
                  {canEditSignatureSection && CAN(Action.CREATE_UNSIGNED_ORDERFORM, Resource.D2C_DEMANDS)&&(
                  <div className={`${styles.footer}`}>
                    {!isPreview && canPreview ? null : <BackButton className={styles.backButton} closePopup={() => setIsPreview(false)} title={"Edit"} disabled = {isSubmit}/>}
                    <Submit className={styles.saveanddownload} title={!isPreview && canPreview ? "Preview" : "Download & Sign"}isSubmitting={isSubmit}/>
                  </div>)}
                </div>
              </div>
              {CAN(Action.CREATE_UNSIGNED_ORDERFORM, Resource.D2C_DEMANDS) && demandDetail?.actionStatus===ACTION_STATUS.ORDER_FORM_UNSIGNED_SAVED_BY_CUSTOMER &&
                <div className={styles.footerwrappercust}>
                  <div className={styles.downloadwrapper} 
                  onClick={() =>generateOrderFormPDF(values, {fileName: `OrderForm_IPX_${values?.customerName || "Customer"}.pdf`,logoSrc: IMAGES.logo,badgeSrc: IMAGES.badge,generatedBy: "Vodafone IPX Portal",})}
                  > 
                    <img src={IMAGES.downloadlogo} alt="download" className={styles.downloadlogo}/>
                    <span className={styles.downloadtxt}>{"Download (PDF)"}</span>
                  </div>
                </div>}
            </Form>
          )}
        </Formik>
        </Accordion>
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    myRights : state.myRights.myRights,
    activeTab :state.tabReducer.activeSecondaryTab,
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    addDemandAction: (payload,id)=>{
      return dispatch(addDemandAction(payload,id))
    },
    openAlertPopup: (alertHeader,alertmessage) => {
      return dispatch(openAlertPopup(alertHeader,alertmessage))
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderForm);